// src/App.js
import React, { useEffect, useState, useContext, lazy, Suspense } from 'react';
import './App.css';
import './DarkMode.css';
import './LightMode.css';
import './components/layout/ResponsiveDesign.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AppThemeContext, ThemeProvider } from './context/AppThemeContext';
import { ProxyContextProvider } from './context/PrereleaseCardContext';
import { DoubleClickProvider } from './context/DoubleClickContext';
import Header from './components/layout/Header';
import GridPage from './components/layout/GridPage';
import { exportGridToWebp, downloadBlob } from './utils/exportGridToWebp';
import Footer from './components/layout/Footer'; // Import the new Footer component
import TryNewDeckToast from './components/layout/TryNewDeckToast';
import DiscordInviteBar from './components/layout/DiscordInviteBar';
import { PageLeaveGuardProvider } from './components/layout/PageLeaveGuardContext';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import EnhancedTCGController from './utils/TCGapi/EnhancedTCGController';
import { isCatalogPath, isGridPath } from './utils/promoChrome';
// FontAwesomeIcon and faGithub are now used in Footer.js, so not strictly needed here unless used elsewhere in App.js
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
// import { faGithub } from '@fortawesome/free-brands-svg-icons';

// Lazy load components for better performance
const EnhancedCardSearchPanel = lazy(() => import('./components/layout/EnhancedCardSearchPanel'));
const DeckViewPanel = lazy(() => import('./components/layout/DeckViewPanel'));
const MobileDeckBuilder = lazy(() => import('./mobile/MobileDeckBuilder'));
const CatalogPage = lazy(() => import('./components/catalog/CatalogPage'));

// Create a loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
    <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

function App() {
  const { theme } = useContext(AppThemeContext);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileVersion, setShowMobileVersion] = useState(false);
  
  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      
      // Check if user wants mobile version (URL parameter or localStorage preference)
      const urlParams = new URLSearchParams(window.location.search);
      const mobileParam = urlParams.get('mobile');
      const mobilePreference = localStorage.getItem('preferMobileVersion');
      
      if (mobileParam === 'true' || mobilePreference === 'true') {
        setShowMobileVersion(true);
      }
      
      return mobile;
    };
    
    checkMobile();
  }, []);
  
  // Initialize the Enhanced TCG Controller
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await EnhancedTCGController.initialize();
        
        const loadSavedDecks = async () => {
          try {
            console.log('App initialized successfully');
          } catch (error) {
            console.error('Error loading saved state:', error);
          }
        };
        
        await loadSavedDecks();
        setIsInitialized(true);
      } catch (error) {
        console.error('Initialization error:', error);
        setInitError(error.message || 'Failed to initialize application');
      }
    };
    
    initializeApp();
    
    return () => {
      // Any cleanup code
    };
  }, []);
  
  // Responsive design adjustments
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); 
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Keep content offset in sync with the real fixed navbar height
  useEffect(() => {
    const navbar = document.querySelector('.navbar.fixed-top, .navbar');
    if (!navbar) return undefined;

    const applyHeight = () => {
      const height = Math.ceil(navbar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--app-header-height', `${height}px`);
    };

    applyHeight();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(applyHeight) : null;
    observer?.observe(navbar);
    window.addEventListener('resize', applyHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', applyHeight);
    };
  }, []);

  // Toggle mobile version
  const toggleMobileVersion = () => {
    const newValue = !showMobileVersion;
    setShowMobileVersion(newValue);
    localStorage.setItem('preferMobileVersion', newValue.toString());
    
    // Update URL parameter
    const url = new URL(window.location);
    if (newValue) {
      url.searchParams.set('mobile', 'true');
    } else {
      url.searchParams.delete('mobile');
    }
    window.history.replaceState({}, '', url);
  };
  
  // Error boundary fallback
  if (initError) {
    return (
      <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
        <Header />
        <div className="container mt-5">
          <Alert variant="danger">
            <Alert.Heading>Initialization Error</Alert.Heading>
            <p>There was a problem initializing the application: {initError}</p>
            <hr />
            <p className="mb-0">
              Please try refreshing the page. If the problem persists, check your internet connection.
            </p>
          </Alert>
        </div>
        {/* Render Footer even on error page for consistency, if desired */}
        <Footer /> 
      </div>
    );
  }
  
  const isGrid = isGridPath();
  const isCatalog = isCatalogPath();

  // Main app rendering
  return (
    <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <Header />
      <div className={`content ${isCatalog ? 'catalog-mode' : ''}`}>
        {isGrid ? (
          <GridPage onExport={async (data, progressCallback) => {
            const blob = await exportGridToWebp(data, 0.92, progressCallback);
            downloadBlob(blob, 'grid.webp');
          }} />
        ) : isCatalog ? (
          <Suspense fallback={<LoadingSpinner />}>
            <CatalogPage />
          </Suspense>
        ) : !isInitialized ? (
          <LoadingSpinner />
        ) : showMobileVersion ? (
          // Mobile Version
          <div className="mobile-version-container">
            <div className="mobile-toggle-bar">
              <Alert variant="info" className="text-center m-0 py-2">
                <small>
                  Mobile Deck Builder Active 
                  <button 
                    className="btn btn-sm btn-outline-primary ms-2"
                    onClick={toggleMobileVersion}
                  >
                    Switch to Desktop
                  </button>
                </small>
              </Alert>
            </div>
            <MobileDeckBuilder />
          </div>
        ) : (
          // Desktop Version
          <ProxyContextProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <div className="enhanced-card-search-panel-container">
                <EnhancedCardSearchPanel />
              </div>
              <div className="deck-view-panel-container">
                <DeckViewPanel />
              </div>
            </Suspense>
          </ProxyContextProvider>
        )}
      </div>

      {!isGrid && !isCatalog && (
        <Footer />
      )}
      <TryNewDeckToast />
      <DiscordInviteBar />
    </div>
  );
}

// Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("App error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-5">
          <Alert variant="danger">
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>{this.state.error?.message || "An unexpected error occurred"}</p>
            <hr />
            <p className="mb-0">
              Please try refreshing the page. If the problem persists, check the console for more details.
            </p>
          </Alert>
           {/* Optionally render footer on error page too */}
          {/* <Footer /> */}
        </div>
      );
    }

    return this.props.children;
  }
}

// App wrapper with all providers
export default function AppWrapper() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <DoubleClickProvider>
          <PageLeaveGuardProvider>
            <App />
          </PageLeaveGuardProvider>
        </DoubleClickProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
